// ABOUTME: Renders the isolated pending inbox for ambiguous WhatsApp contact episodes.
// ABOUTME: Keeps episode reading, search, media, pagination, and resolution separate from applicant chats.
import React from "react";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Paper,
    TextField,
    Tooltip,
    Typography
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import DescriptionIcon from "@material-ui/icons/Description";
import RefreshIcon from "@material-ui/icons/Refresh";
import SearchIcon from "@material-ui/icons/Search";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { apiDirectCall } from "customHooks/useApi";
import { getWhatsappApiResponseData } from "functions/whatsappApiResponse";
import { createWhatsappActionRequestTracker } from "functions/whatsappContactEpisode";
import {
    formatWhatsappInboxDate,
    getWhatsappContactEpisodeConversationReference,
    isSameWhatsappContactEpisode,
    maskWhatsappContactEpisodePhone,
    shouldApplyWhatsappContactEpisodeResponse,
    WhatsappContactEpisodeMode
} from "./helpers";

type Translate = (key: string, values?: Compleo.IObject) => string;

interface WhatsappContactEpisodePendingProps {
    companyId: number;
    mode: Extract<WhatsappContactEpisodeMode, "READ_ONLY" | "ACTIVE">;
    t: Translate;
    language?: string;
    onPendingCountChange?: (count: number) => void;
    realtimeRevision?: number;
}

interface ContactEpisodeIdentity {
    contactEpisodeId: string;
    stateRevision: number;
}

const getContactEpisodeIdentity = (
    episode: Compleo.IObject
): ContactEpisodeIdentity => ({
    contactEpisodeId: String(episode.contactEpisodeId || ""),
    stateRevision: Number(episode.stateRevision || 0)
});

const isPendingContactEpisode = (episode: Compleo.IObject) =>
    episode.scope === "CONTACT_EPISODE" &&
    (episode.state === "PENDING_ACTIVE" ||
        episode.state === "PENDING_EXPIRED") &&
    Boolean(episode.contactEpisodeId);

const mergeContactEpisodes = (
    current: Compleo.IObject[],
    loaded: Compleo.IObject[],
    append: boolean
) => {
    const episodes = new Map<string, Compleo.IObject>();
    if (append) {
        current.forEach((episode) =>
            episodes.set(String(episode.contactEpisodeId), episode)
        );
    }
    loaded
        .filter(isPendingContactEpisode)
        .forEach((episode) =>
            episodes.set(String(episode.contactEpisodeId), episode)
        );
    return Array.from(episodes.values());
};

const getContactEpisodeMessageText = (item: Compleo.IObject) =>
    String(
        item.text?.body ||
            item.text ||
            item.body ||
            item.caption ||
            item.message ||
            ""
    );

const isContactEpisodeMedia = (item: Compleo.IObject) =>
    ["image", "document", "video", "audio", "sticker"].includes(
        String(item.type || "").toLowerCase()
    );

const getContactEpisodeCandidateContext = (candidate: Compleo.IObject) =>
    (Array.isArray(candidate.jobContext) ? candidate.jobContext : [])
        .map((context: Compleo.IObject) =>
            [context.jobTitle, context.branchName]
                .map((value) => String(value || "").trim())
                .filter(Boolean)
                .join(" — ")
        )
        .filter(Boolean)
        .join(", ");

export const WhatsappContactEpisodePending = (
    props: WhatsappContactEpisodePendingProps
) => {
    const {
        companyId,
        language = "pt-BR",
        mode,
        onPendingCountChange,
        realtimeRevision = 0,
        t
    } = props;
    const [episodes, setEpisodes] = React.useState<Compleo.IObject[]>([]);
    const [selectedEpisode, setSelectedEpisode] =
        React.useState<Compleo.IObject>({});
    const [detail, setDetail] = React.useState<Compleo.IObject>({});
    const [candidates, setCandidates] = React.useState<Compleo.IObject[]>([]);
    const [selectedApplicantId, setSelectedApplicantId] = React.useState("");
    const [nextCursor, setNextCursor] = React.useState("");
    const [listSearch, setListSearch] = React.useState("");
    const [appliedListSearch, setAppliedListSearch] = React.useState("");
    const [messageSearch, setMessageSearch] = React.useState("");
    const [messageMatches, setMessageMatches] = React.useState<
        Compleo.IObject[]
    >([]);
    const [messageSearchExecuted, setMessageSearchExecuted] =
        React.useState(false);
    const [loadingList, setLoadingList] = React.useState(false);
    const [loadingDetail, setLoadingDetail] = React.useState(false);
    const [loadingMessages, setLoadingMessages] = React.useState(false);
    const [resolving, setResolving] = React.useState(false);
    const [error, setError] = React.useState("");
    const [candidateSearchText, setCandidateSearchText] = React.useState("");
    const [searchedCandidates, setSearchedCandidates] = React.useState<
        Compleo.IObject[]
    >([]);
    const [loadingCandidateSearch, setLoadingCandidateSearch] =
        React.useState(false);
    const listRequestRef = React.useRef(0);
    const detailRequestRef = React.useRef(0);
    const messageRequestRef = React.useRef(0);
    const resolutionRequestRef = React.useRef(0);
    const pendingCountRef = React.useRef(0);
    const candidateSearchRequestRef = React.useRef(0);
    const selectedIdentityRef = React.useRef<ContactEpisodeIdentity | null>(
        null
    );
    const realtimeRevisionRef = React.useRef(realtimeRevision);
    const actionRequestTrackerRef = React.useRef(
        createWhatsappActionRequestTracker()
    );

    const resetSelectedEpisode = React.useCallback(() => {
        detailRequestRef.current += 1;
        messageRequestRef.current += 1;
        resolutionRequestRef.current += 1;
        candidateSearchRequestRef.current += 1;
        selectedIdentityRef.current = null;
        setSelectedEpisode({});
        setDetail({});
        setCandidates([]);
        setSelectedApplicantId("");
        setMessageSearch("");
        setMessageMatches([]);
        setMessageSearchExecuted(false);
        setCandidateSearchText("");
        setSearchedCandidates([]);
        setLoadingCandidateSearch(false);
        setLoadingDetail(false);
        setLoadingMessages(false);
        setResolving(false);
    }, []);

    const refreshList = React.useCallback(
        async (cursor = "") => {
            if (!companyId) {
                return;
            }
            const requestId = listRequestRef.current + 1;
            listRequestRef.current = requestId;
            setLoadingList(true);
            setError("");
            try {
                const result = await apiDirectCall(
                    "/whatsapp/conversations",
                    "post",
                    {
                        companyId,
                        scope: "CONTACT_EPISODE",
                        search: appliedListSearch,
                        cursor,
                        limit: 50
                    }
                );
                if (listRequestRef.current !== requestId) {
                    return;
                }
                const data = getWhatsappApiResponseData(result);
                const loaded = Array.isArray(data.conversations)
                    ? data.conversations
                    : [];
                const loadedEpisodes = loaded.filter(isPendingContactEpisode);
                setEpisodes((current) =>
                    mergeContactEpisodes(
                        current,
                        loadedEpisodes,
                        Boolean(cursor)
                    )
                );
                if (!cursor && selectedIdentityRef.current) {
                    const currentSelection = selectedIdentityRef.current;
                    const selectedListEpisode = loadedEpisodes.find((episode) =>
                        isSameWhatsappContactEpisode(
                            currentSelection,
                            getContactEpisodeIdentity(episode)
                        )
                    );
                    if (!selectedListEpisode) {
                        resetSelectedEpisode();
                    } else {
                        const nextIdentity =
                            getContactEpisodeIdentity(selectedListEpisode);
                        if (
                            shouldApplyWhatsappContactEpisodeResponse(
                                currentSelection,
                                nextIdentity
                            )
                        ) {
                            selectedIdentityRef.current = nextIdentity;
                            setSelectedEpisode((current) => ({
                                ...current,
                                ...selectedListEpisode
                            }));
                        }
                    }
                }
                setNextCursor(String(data.nextCursor || ""));
                const totalPending = Number.isFinite(Number(data.totalPending))
                    ? Number(data.totalPending)
                    : loadedEpisodes.length;
                pendingCountRef.current = totalPending;
                onPendingCountChange?.(totalPending);
            } catch {
                if (listRequestRef.current === requestId) {
                    setError(t("loadPendingContactEpisodesError"));
                }
            } finally {
                if (listRequestRef.current === requestId) {
                    setLoadingList(false);
                }
            }
        },
        [
            appliedListSearch,
            companyId,
            onPendingCountChange,
            resetSelectedEpisode,
            t
        ]
    );

    React.useEffect(() => {
        resetSelectedEpisode();
        setEpisodes([]);
        setNextCursor("");
        void refreshList();
    }, [appliedListSearch, companyId, refreshList, resetSelectedEpisode]);

    const refreshDetail = React.useCallback(async () => {
        const selection = selectedIdentityRef.current;
        if (!companyId || !selection?.contactEpisodeId) {
            return;
        }
        const requestId = detailRequestRef.current + 1;
        detailRequestRef.current = requestId;
        setLoadingDetail(true);
        setError("");
        const conversationReference =
            getWhatsappContactEpisodeConversationReference(
                selection.contactEpisodeId
            );
        try {
            const detailResult = await apiDirectCall(
                "/whatsapp/conversation",
                "post",
                {
                    companyId,
                    conversationReference,
                    limit: 200,
                    includeInternalNotes: true
                }
            );
            const nextDetail = getWhatsappApiResponseData(detailResult);
            if (
                detailRequestRef.current !== requestId ||
                !selectedIdentityRef.current ||
                !shouldApplyWhatsappContactEpisodeResponse(
                    selectedIdentityRef.current,
                    nextDetail
                )
            ) {
                return;
            }
            const nextIdentity = getContactEpisodeIdentity(nextDetail);
            selectedIdentityRef.current = nextIdentity;
            setSelectedEpisode((current) => ({
                ...current,
                ...nextDetail,
                unreadCount: 0
            }));
            setDetail(nextDetail);
            setCandidates([]);
            try {
                const candidatesResult = await apiDirectCall(
                    "/whatsapp/contactepisodecandidates",
                    "post",
                    {
                        companyId,
                        conversationReference
                    }
                );
                const nextCandidates =
                    getWhatsappApiResponseData(candidatesResult);
                if (
                    detailRequestRef.current !== requestId ||
                    !selectedIdentityRef.current ||
                    !shouldApplyWhatsappContactEpisodeResponse(
                        selectedIdentityRef.current,
                        nextDetail
                    )
                ) {
                    return;
                }
                setCandidates(
                    Array.isArray(nextCandidates.candidates)
                        ? nextCandidates.candidates
                        : []
                );
            } catch {
                if (detailRequestRef.current === requestId) {
                    setError(t("loadContactEpisodeCandidatesError"));
                }
            }
        } catch {
            if (detailRequestRef.current === requestId) {
                setError(t("loadContactEpisodeError"));
            }
        } finally {
            if (detailRequestRef.current === requestId) {
                setLoadingDetail(false);
            }
        }
    }, [companyId, t]);

    const selectEpisode = React.useCallback(
        (episode: Compleo.IObject) => {
            const identity = getContactEpisodeIdentity(episode);
            if (!identity.contactEpisodeId) {
                return;
            }
            detailRequestRef.current += 1;
            messageRequestRef.current += 1;
            resolutionRequestRef.current += 1;
            candidateSearchRequestRef.current += 1;
            selectedIdentityRef.current = identity;
            setSelectedEpisode({ ...episode, unreadCount: 0 });
            setEpisodes((current) =>
                current.map((item) =>
                    item.contactEpisodeId === identity.contactEpisodeId
                        ? { ...item, unreadCount: 0 }
                        : item
                )
            );
            setDetail({});
            setCandidates([]);
            setSelectedApplicantId("");
            setMessageSearch("");
            setMessageMatches([]);
            setMessageSearchExecuted(false);
            setCandidateSearchText("");
            setSearchedCandidates([]);
            setLoadingCandidateSearch(false);
            void refreshDetail();
        },
        [refreshDetail]
    );

    React.useEffect(() => {
        if (realtimeRevision <= realtimeRevisionRef.current) {
            return;
        }
        realtimeRevisionRef.current = realtimeRevision;
        void refreshList();
        if (selectedIdentityRef.current) {
            void refreshDetail();
        }
    }, [realtimeRevision, refreshDetail, refreshList]);

    const searchMessages = React.useCallback(async () => {
        const selection = selectedIdentityRef.current;
        const search = messageSearch.trim();
        if (!companyId || !selection?.contactEpisodeId || search.length < 4) {
            messageRequestRef.current += 1;
            setMessageMatches([]);
            setMessageSearchExecuted(false);
            return;
        }
        const requestId = messageRequestRef.current + 1;
        messageRequestRef.current = requestId;
        setLoadingMessages(true);
        setMessageSearchExecuted(true);
        try {
            const result = await apiDirectCall(
                "/whatsapp/searchmessages",
                "post",
                {
                    companyId,
                    conversationReference:
                        getWhatsappContactEpisodeConversationReference(
                            selection.contactEpisodeId
                        ),
                    search,
                    includeInternalNotes: true,
                    limit: 100
                }
            );
            if (
                messageRequestRef.current === requestId &&
                selectedIdentityRef.current &&
                isSameWhatsappContactEpisode(
                    selectedIdentityRef.current,
                    selection
                )
            ) {
                const data = getWhatsappApiResponseData(result);
                setMessageMatches(
                    Array.isArray(data.matches) ? data.matches : []
                );
            }
        } catch {
            if (messageRequestRef.current === requestId) {
                setError(t("searchMessagesError"));
            }
        } finally {
            if (messageRequestRef.current === requestId) {
                setLoadingMessages(false);
            }
        }
    }, [companyId, messageSearch, t]);

    const openMedia = React.useCallback(
        async (item: Compleo.IObject) => {
            const selection = selectedIdentityRef.current;
            if (!selection?.contactEpisodeId || !item.messageId) {
                return;
            }
            const mediaWindow = window.open("", "_blank");
            if (mediaWindow) {
                mediaWindow.opener = null;
            }
            try {
                const result = await apiDirectCall(
                    "/whatsapp/mediaurl",
                    "post",
                    {
                        companyId,
                        conversationReference:
                            getWhatsappContactEpisodeConversationReference(
                                selection.contactEpisodeId
                            ),
                        messageId: item.messageId
                    }
                );
                const url = getWhatsappApiResponseData(result).url;
                if (mediaWindow) {
                    mediaWindow.location.href = url;
                } else {
                    window.open(url, "_blank", "noopener,noreferrer");
                }
            } catch {
                mediaWindow?.close();
                setError(t("openMediaError"));
            }
        },
        [companyId, t]
    );

    const resolveEpisode = React.useCallback(async () => {
        const selection = selectedIdentityRef.current;
        if (
            mode !== "ACTIVE" ||
            !selection?.contactEpisodeId ||
            !selectedApplicantId
        ) {
            return;
        }
        const requestId = resolutionRequestRef.current + 1;
        resolutionRequestRef.current = requestId;
        setResolving(true);
        setError("");
        const attempt = actionRequestTrackerRef.current.prepare(
            "RESOLVE_EPISODE",
            JSON.stringify({
                companyId,
                contactEpisodeId: selection.contactEpisodeId,
                targetApplicantId: selectedApplicantId,
                expectedStateRevision: selection.stateRevision
            })
        );
        try {
            await apiDirectCall("/whatsapp/resolvecontactepisode", "post", {
                companyId,
                conversationReference:
                    getWhatsappContactEpisodeConversationReference(
                        selection.contactEpisodeId
                    ),
                targetApplicantId: selectedApplicantId,
                expectedStateRevision: selection.stateRevision,
                actionRequestId: attempt.actionRequestId
            });
            actionRequestTrackerRef.current.complete(
                "RESOLVE_EPISODE",
                attempt.signature
            );
            if (
                resolutionRequestRef.current !== requestId ||
                !selectedIdentityRef.current ||
                !isSameWhatsappContactEpisode(
                    selectedIdentityRef.current,
                    selection
                )
            ) {
                return;
            }
            setEpisodes((current) =>
                current.filter(
                    (episode) =>
                        episode.contactEpisodeId !== selection.contactEpisodeId
                )
            );
            pendingCountRef.current = Math.max(0, pendingCountRef.current - 1);
            onPendingCountChange?.(pendingCountRef.current);
            resetSelectedEpisode();
        } catch {
            if (resolutionRequestRef.current === requestId) {
                setError(t("resolveContactEpisodeError"));
            }
        } finally {
            if (resolutionRequestRef.current === requestId) {
                setResolving(false);
            }
        }
    }, [
        companyId,
        mode,
        onPendingCountChange,
        resetSelectedEpisode,
        selectedApplicantId,
        t
    ]);

    const selectedIdentity = selectedIdentityRef.current;
    const visibleItems = messageSearchExecuted
        ? messageMatches
        : Array.isArray(detail.items)
          ? detail.items
          : [];
    const selectedPhone = maskWhatsappContactEpisodePhone(
        detail.maskedPhone ||
            selectedEpisode.maskedPhone ||
            selectedEpisode.remotePhone
    );

    return (
        <Paper style={{ height: "100%", overflow: "hidden" }}>
            <Box display="flex" style={{ height: "100%", minHeight: 520 }}>
                <Box
                    display="flex"
                    flexDirection="column"
                    width={380}
                    style={{ borderRight: "1px solid #e0e0e0" }}
                >
                    <Box padding={2}>
                        <Box display="flex" alignItems="center" gridGap={8}>
                            <Typography variant="h6">
                                {t("pendingContactEpisodes")}
                            </Typography>
                            <Box flex={1} />
                            <Tooltip title={t("refreshConversations")}>
                                <IconButton
                                    size="small"
                                    aria-label={t("refreshConversations")}
                                    onClick={() => void refreshList()}
                                >
                                    <RefreshIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Box display="flex" marginTop={1} gridGap={8}>
                            <TextField
                                size="small"
                                variant="outlined"
                                value={listSearch}
                                onChange={(event) =>
                                    setListSearch(event.target.value)
                                }
                                inputProps={{
                                    "aria-label": t(
                                        "searchPendingContactEpisodes"
                                    )
                                }}
                                fullWidth
                            />
                            <Tooltip title={t("searchPendingContactEpisodes")}>
                                <IconButton
                                    size="small"
                                    aria-label={t(
                                        "searchPendingContactEpisodes"
                                    )}
                                    onClick={() =>
                                        setAppliedListSearch(listSearch.trim())
                                    }
                                >
                                    <SearchIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                    <Divider />
                    <List style={{ flex: 1, overflow: "auto" }}>
                        {episodes.map((episode) => {
                            const episodeId = String(episode.contactEpisodeId);
                            const phone = maskWhatsappContactEpisodePhone(
                                episode.maskedPhone || episode.remotePhone
                            );
                            return (
                                <ListItem
                                    button
                                    key={episodeId}
                                    selected={
                                        selectedIdentity?.contactEpisodeId ===
                                        episodeId
                                    }
                                    data-contact-episode-id={episodeId}
                                    onClick={() => selectEpisode(episode)}
                                >
                                    <ListItemText
                                        primary={phone}
                                        secondary={
                                            <>
                                                {t(
                                                    episode.state ===
                                                        "PENDING_EXPIRED"
                                                        ? "expiredContactEpisode"
                                                        : "activeContactEpisode"
                                                )}
                                                {episode.lastActivityAt
                                                    ? ` · ${formatWhatsappInboxDate(
                                                          episode.lastActivityAt,
                                                          language
                                                      )}`
                                                    : ""}
                                            </>
                                        }
                                    />
                                    {Number(episode.unreadCount || 0) > 0 && (
                                        <Chip
                                            size="small"
                                            label={Number(episode.unreadCount)}
                                        />
                                    )}
                                </ListItem>
                            );
                        })}
                        {nextCursor && (
                            <ListItem>
                                <Button
                                    disabled={loadingList}
                                    onClick={() => void refreshList(nextCursor)}
                                >
                                    {t("loadMore")}
                                </Button>
                            </ListItem>
                        )}
                        {!loadingList && episodes.length === 0 && (
                            <ListItem>
                                <ListItemText
                                    primary={t("pendingContactEpisodesEmpty")}
                                />
                            </ListItem>
                        )}
                        {loadingList && (
                            <ListItem>
                                <CircularProgress size={20} />
                            </ListItem>
                        )}
                    </List>
                </Box>
                <Box
                    display="flex"
                    flexDirection="column"
                    flex={1}
                    minWidth={0}
                >
                    {error && <Alert severity="error">{error}</Alert>}
                    {selectedIdentity ? (
                        <>
                            <Box padding={2}>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    gridGap={8}
                                >
                                    <Typography variant="subtitle1">
                                        {selectedPhone}
                                    </Typography>
                                    <Chip
                                        size="small"
                                        label={t(
                                            selectedEpisode.state ===
                                                "PENDING_EXPIRED"
                                                ? "expiredContactEpisode"
                                                : "activeContactEpisode"
                                        )}
                                    />
                                </Box>
                            </Box>
                            <Divider />
                            <Box
                                display="flex"
                                flexDirection="column"
                                flex={1}
                                overflow="auto"
                                padding={2}
                            >
                                <Box
                                    display="flex"
                                    gridGap={8}
                                    marginBottom={2}
                                >
                                    <TextField
                                        size="small"
                                        variant="outlined"
                                        value={messageSearch}
                                        onChange={(event) =>
                                            setMessageSearch(event.target.value)
                                        }
                                        inputProps={{
                                            "aria-label": t(
                                                "searchThisConversation"
                                            )
                                        }}
                                        fullWidth
                                    />
                                    <Tooltip title={t("searchMessages")}>
                                        <IconButton
                                            size="small"
                                            disabled={loadingMessages}
                                            aria-label={t("searchMessages")}
                                            onClick={() =>
                                                void searchMessages()
                                            }
                                        >
                                            {loadingMessages ? (
                                                <CircularProgress size={18} />
                                            ) : (
                                                <SearchIcon />
                                            )}
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                {visibleItems.map((item) => (
                                    <Box
                                        key={item.messageId || item.createdAt}
                                        marginBottom={1}
                                        padding={1}
                                        style={{
                                            alignSelf:
                                                item.direction === "OUTGOING"
                                                    ? "flex-end"
                                                    : "flex-start",
                                            backgroundColor:
                                                item.direction === "OUTGOING"
                                                    ? "#dcf8c6"
                                                    : "#ffffff",
                                            border: "1px solid #e0e0e0",
                                            borderRadius: 8,
                                            maxWidth: "80%"
                                        }}
                                    >
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gridGap={8}
                                        >
                                            {isContactEpisodeMedia(item) && (
                                                <DescriptionIcon fontSize="small" />
                                            )}
                                            <Typography variant="body2">
                                                {getContactEpisodeMessageText(
                                                    item
                                                ) || t("file")}
                                            </Typography>
                                            {isContactEpisodeMedia(item) &&
                                                item.hasMedia && (
                                                    <Tooltip
                                                        title={t("openFile")}
                                                    >
                                                        <IconButton
                                                            size="small"
                                                            aria-label={t(
                                                                "openFile"
                                                            )}
                                                            onClick={() =>
                                                                void openMedia(
                                                                    item
                                                                )
                                                            }
                                                        >
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                        </Box>
                                        <Typography variant="caption">
                                            {formatWhatsappInboxDate(
                                                item.metaTimestamp ||
                                                    item.createdAt,
                                                language
                                            )}
                                        </Typography>
                                    </Box>
                                ))}
                                {messageSearchExecuted &&
                                    !loadingMessages &&
                                    visibleItems.length === 0 && (
                                        <Typography variant="body2">
                                            {t("noMessagesFound")}
                                        </Typography>
                                    )}
                                {loadingDetail && (
                                    <CircularProgress size={20} />
                                )}
                            </Box>
                            <Divider />
                            <Box padding={2}>
                                <Typography variant="subtitle2">
                                    {t("contactEpisodeCandidates")}
                                </Typography>
                                {candidates.length === 0 && !loadingDetail && (
                                    <Box marginTop={1} display="flex" flexDirection="column" gridGap={8}>
                                        <Typography variant="body2" color="textSecondary">
                                            {t("noContactEpisodeCandidates")}
                                        </Typography>
                                        <TextField
                                            size="small"
                                            variant="outlined"
                                            fullWidth
                                            label={t("searchCandidate")}
                                            value={candidateSearchText}
                                            helperText={
                                                candidateSearchText.trim().length > 0 &&
                                                candidateSearchText.trim().length < 4
                                                    ? t("searchCandidateMinLength")
                                                    : undefined
                                            }
                                            onChange={async (event) => {
                                                const text = event.target.value;
                                                setCandidateSearchText(text);
                                                setSelectedApplicantId("");
                                                const requestId = candidateSearchRequestRef.current + 1;
                                                candidateSearchRequestRef.current = requestId;
                                                if (!companyId || text.trim().length < 4) {
                                                    setSearchedCandidates([]);
                                                    setLoadingCandidateSearch(false);
                                                    return;
                                                }
                                                setLoadingCandidateSearch(true);
                                                try {
                                                    const result = await apiDirectCall(
                                                        "/whatsapp/searchcontacts",
                                                        "post",
                                                        {
                                                            companyId,
                                                            search: text.trim()
                                                        }
                                                    );
                                                    if (candidateSearchRequestRef.current !== requestId) {
                                                        return;
                                                    }
                                                    const data = getWhatsappApiResponseData(result);
                                                    setSearchedCandidates(
                                                        Array.isArray(data.results) ? data.results : []
                                                    );
                                                } catch {
                                                    if (candidateSearchRequestRef.current === requestId) {
                                                        setSearchedCandidates([]);
                                                    }
                                                } finally {
                                                    if (candidateSearchRequestRef.current === requestId) {
                                                        setLoadingCandidateSearch(false);
                                                    }
                                                }
                                            }}
                                        />
                                        {loadingCandidateSearch && (
                                            <Box display="flex" alignItems="center" gridGap={8}>
                                                <CircularProgress size={16} />
                                                <Typography variant="caption">{t("searchingCandidates")}</Typography>
                                            </Box>
                                        )}
                                        {searchedCandidates.length > 0 && (
                                            <TextField
                                                select
                                                fullWidth
                                                size="small"
                                                variant="outlined"
                                                label={t("candidate")}
                                                value={selectedApplicantId}
                                                onChange={(event) =>
                                                    setSelectedApplicantId(event.target.value)
                                                }
                                            >
                                                <MenuItem value="">
                                                    {t("selectContactEpisodeCandidate")}
                                                </MenuItem>
                                                {searchedCandidates.map((candidate) => (
                                                    <MenuItem
                                                        key={String(candidate.applicantId)}
                                                        value={String(candidate.applicantId)}
                                                    >
                                                        {String(candidate.name || "")} ({String(candidate.email || candidate.emailMasked || "")})
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                        {selectedApplicantId && (
                                            <Button
                                                component="a"
                                                href={`/applicant/view/${selectedApplicantId}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                size="small"
                                            >
                                                {t("candidateProfile")}
                                            </Button>
                                        )}
                                    </Box>
                                )}
                                {candidates.length > 0 && (
                                    <>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            variant="outlined"
                                            label={t("candidate")}
                                            value={selectedApplicantId}
                                            onChange={(event) =>
                                                setSelectedApplicantId(
                                                    event.target.value
                                                )
                                            }
                                        >
                                            <MenuItem value="">
                                                {t(
                                                    "selectContactEpisodeCandidate"
                                                )}
                                            </MenuItem>
                                            {candidates.map((candidate) => {
                                                const candidateContext =
                                                    getContactEpisodeCandidateContext(
                                                        candidate
                                                    );
                                                return (
                                                    <MenuItem
                                                        key={String(
                                                            candidate.applicantId
                                                        )}
                                                        value={String(
                                                            candidate.applicantId
                                                        )}
                                                    >
                                                        {String(
                                                            candidate.name || ""
                                                        )}{" "}
                                                        ·{" "}
                                                        {String(
                                                            candidate.emailMasked ||
                                                                ""
                                                        )}
                                                        {candidateContext
                                                            ? ` · ${candidateContext}`
                                                            : ""}
                                                    </MenuItem>
                                                );
                                            })}
                                        </TextField>
                                        {selectedApplicantId && (
                                            <Button
                                                component="a"
                                                href={`/applicant/view/${selectedApplicantId}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                size="small"
                                            >
                                                {t("candidateProfile")}
                                            </Button>
                                        )}
                                    </>
                                )}
                                {mode === "READ_ONLY" ? (
                                    <Box marginTop={1}>
                                        <Alert severity="info">
                                            {t(
                                                "pendingContactEpisodesReadOnly"
                                            )}
                                        </Alert>
                                    </Box>
                                ) : (
                                    <Box marginTop={1}>
                                        <Button
                                            color="primary"
                                            variant="contained"
                                            disabled={
                                                !selectedApplicantId ||
                                                resolving
                                            }
                                            onClick={() =>
                                                void resolveEpisode()
                                            }
                                        >
                                            {t("resolveContactEpisode")}
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        </>
                    ) : (
                        <Box padding={3}>
                            <Alert severity="info">
                                {t("selectContactEpisode")}
                            </Alert>
                        </Box>
                    )}
                </Box>
            </Box>
        </Paper>
    );
};

export default WhatsappContactEpisodePending;
